import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Mail, Lock, User, Eye, EyeOff, ShieldCheck, Globe,
  TrendingUp, BarChart3, Briefcase, Check
} from 'lucide-react';
import { setCachedPortfolioMap, setCachedWatchlistMap, clearCachedPortfolioMap, clearCachedWatchlistMap } from '../utils/portfolioWatchlistCache';
import { clearCachedFilterPresets, hydrateFilterPresets } from '../utils/filterPresetsCache';
import { authFetch } from '../utils/authFetch';
import { persistTokens, startAuthSessionRefresh } from '../utils/authTokens';
import BidsigLogo from './BidsigLogo';

const COUNTRY_OPTIONS = [
  { value: 'usa', label: 'United States' },
  { value: 'canada', label: 'Canada' },
];
const ALLOWED_COUNTRIES = COUNTRY_OPTIONS.map((o) => o.value);

const emptyForm = () => ({
  first_name: '',
  last_name: '',
  country: '',
  email: '',
  password: ''
});

const Login = ({ setUser }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(() => searchParams.get('mode') !== 'signup');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showForgotConfirm, setShowForgotConfirm] = useState(false);
  const [showNewPasswordRequired, setShowNewPasswordRequired] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [confirmationEmail, setConfirmationEmail] = useState('');
  const [pendingPassword, setPendingPassword] = useState('');
  const [pendingCountry, setPendingCountry] = useState('');
  const [challengeSession, setChallengeSession] = useState('');
  const [challengeUsername, setChallengeUsername] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const messageTimerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => () => {
    if (messageTimerRef.current) clearTimeout(messageTimerRef.current);
  }, []);

  useEffect(() => {
    setIsLogin(searchParams.get('mode') !== 'signup');
  }, [searchParams]);

  const passwordChecks = useMemo(() => {
    const pw = formData.password;
    return {
      minLength: pw.length >= 8,
      hasNumber: /\d/.test(pw),
      hasSpecial: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(pw),
      hasUppercase: /[A-Z]/.test(pw),
      hasLowercase: /[a-z]/.test(pw),
    };
  }, [formData.password]);

  const allPasswordChecksPassed = useMemo(() => {
    return Object.values(passwordChecks).every(Boolean);
  }, [passwordChecks]);

  const isValidName = (value) => {
    const trimmed = (value || '').trim();
    const letters = trimmed.match(/[A-Za-z]/g) || [];
    return letters.length >= 2;
  };

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || '').trim());

  const canCreateAccount = useMemo(() => {
    return (
      isValidName(formData.first_name) &&
      isValidName(formData.last_name) &&
      ALLOWED_COUNTRIES.includes(formData.country) &&
      isValidEmail(formData.email) &&
      allPasswordChecksPassed
    );
  }, [formData.first_name, formData.last_name, formData.country, formData.email, allPasswordChecksPassed]);

  const canSubmitNewPassword = useMemo(() => {
    return allPasswordChecksPassed && formData.password === confirmPassword && confirmPassword.length > 0;
  }, [allPasswordChecksPassed, formData.password, confirmPassword]);

  const showNameLengthHint = useMemo(() => {
    const nameHasOneLetter = (value) => {
      const letters = ((value || '').match(/[A-Za-z]/g) || []).length;
      return letters === 1;
    };
    return nameHasOneLetter(formData.first_name) || nameHasOneLetter(formData.last_name);
  }, [formData.first_name, formData.last_name]);

  const showMessage = (type, text, { persist = false } = {}) => {
    if (messageTimerRef.current) {
      clearTimeout(messageTimerRef.current);
      messageTimerRef.current = null;
    }
    setMessage({ type, text });
    // Keep errors and important notices visible longer so users can read them
    if (!persist && type !== 'error') {
      const duration = type === 'info' ? 8000 : 6000;
      messageTimerRef.current = setTimeout(() => setMessage({ type: '', text: '' }), duration);
    }
  };

  const clearMessage = () => {
    if (messageTimerRef.current) {
      clearTimeout(messageTimerRef.current);
      messageTimerRef.current = null;
    }
    setMessage({ type: '', text: '' });
  };

  const resetToSignIn = (email = '') => {
    setShowConfirmation(false);
    setShowForgotPassword(false);
    setShowForgotConfirm(false);
    setShowNewPasswordRequired(false);
    setConfirmationCode('');
    setConfirmationEmail('');
    setPendingPassword('');
    setPendingCountry('');
    setChallengeSession('');
    setChallengeUsername('');
    setConfirmPassword('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setIsLogin(true);
    setFormData({ ...emptyForm(), email: email || '' });
    setErrors({});
    setSearchParams({}, { replace: true });
  };

  const getAuthErrorMessage = (data, mode) => {
    const fallback = mode === 'login'
      ? 'Unable to sign in right now. Please try again. If this keeps happening, contact support.'
      : mode === 'forgot'
        ? 'Unable to reset your password right now. Please try again. If this keeps happening, contact support.'
        : 'Unable to create your account right now. Please try again. If this keeps happening, contact support.';

    if (!data) return fallback;

    const code = data.code || data.errorCode;
    const raw = (data.message || '').trim();

    // User-actionable auth issues only — never surface admin/infra guidance
    if (data.needsConfirmation) {
      return 'Your email is not verified yet. Enter the confirmation code we sent, or request a new one.';
    }
    if (data.needsNewPassword) {
      return 'For security, you need to set a new password before signing in.';
    }
    if (code === 'NotAuthorizedException' || /invalid email or password/i.test(raw)) {
      return 'Incorrect email or password. Check your credentials and try again.';
    }
    if (code === 'UserNotFoundException' || /^user not found$/i.test(raw)) {
      return 'No account found with that email. Create an account first, or check for typos.';
    }
    if (code === 'UsernameExistsException' || /email already exists/i.test(raw)) {
      return 'An account with this email already exists. Sign in instead, or use a different email.';
    }
    if (code === 'InvalidPasswordException' || /password does not meet|password must (be|contain)/i.test(raw)) {
      return 'Password does not meet the requirements listed below.';
    }
    if (/unable to start password reset|unable to prepare account/i.test(raw)) {
      return 'Unable to start password reset for this account. Please contact support.';
    }
    if (code === 'CodeMismatchException' || /invalid confirmation code/i.test(raw)) {
      return 'That confirmation code is incorrect. Double-check the code in your email and try again.';
    }
    if (code === 'ExpiredCodeException' || /confirmation code has expired|session expired/i.test(raw)) {
      return mode === 'forgot' || mode === 'newPassword'
        ? 'That code or session has expired. Request a new one and try again.'
        : 'That confirmation code has expired. Tap “Resend code” to get a new one.';
    }
    if (code === 'TooManyRequestsException' || code === 'LimitExceededException' || code === 'TooManyFailedAttemptsException' || /too many/i.test(raw)) {
      return 'Too many attempts. Please wait a few minutes and try again.';
    }
    if (/user profile not found/i.test(raw)) {
      return 'We could not load your account profile. Please try again or contact support.';
    }
    if (/first name must be at least 2/i.test(raw)) {
      return 'First name must be at least 2 letters.';
    }
    if (/last name must be at least 2/i.test(raw)) {
      return 'Last name must be at least 2 letters.';
    }
    if (/country must be/i.test(raw) || /^please select your country/i.test(raw)) {
      return 'Please select your country (United States or Canada).';
    }
    if (/invalid email format/i.test(raw) || /email is required/i.test(raw) || /email and password are required/i.test(raw)) {
      return mode === 'login'
        ? 'Enter a valid email and password to sign in.'
        : 'Enter a valid email address to continue.';
    }
    if (/all fields are required/i.test(raw)) {
      return 'Please complete all required fields and try again.';
    }

    // Anything else (Cognito config, AWS console steps, server errors, raw exception text) → generic
    return fallback;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
    if (message.type === 'error') {
      // Keep success/info notices; clear stale errors when the user edits
      clearMessage();
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!isLogin) {
      if (!formData.first_name.trim()) {
        newErrors.first_name = 'First name is required';
      } else if (!isValidName(formData.first_name)) {
        newErrors.first_name = 'First name must be at least 2 letters';
      }
      if (!formData.last_name.trim()) {
        newErrors.last_name = 'Last name is required';
      } else if (!isValidName(formData.last_name)) {
        newErrors.last_name = 'Last name must be at least 2 letters';
      }
      if (!formData.country) {
        newErrors.country = 'Please select your country';
      } else if (!ALLOWED_COUNTRIES.includes(formData.country)) {
        newErrors.country = 'Country must be United States or Canada';
      }
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (!isLogin && !allPasswordChecksPassed) {
      newErrors.password = 'Password does not meet all requirements';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      const firstError = Object.values(newErrors)[0];
      showMessage('error', firstError, { persist: true });
      return false;
    }
    return true;
  };

  const validateNewPasswordForm = ({ requireCode = false } = {}) => {
    const newErrors = {};

    if (requireCode && !confirmationCode.trim()) {
      newErrors.confirmationCode = 'Enter the 6-digit code from your email';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (!allPasswordChecksPassed) {
      newErrors.password = 'Password does not meet all requirements';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Confirm your new password';
    } else if (formData.password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      const firstError = Object.values(newErrors)[0];
      showMessage('error', firstError, { persist: true });
      return false;
    }
    return true;
  };

  const handleLoginSuccess = (data) => {
    const userData = {
      id: data.id,
      email: data.email,
      phone: data.phone,
      country: data.country || '',
      first_name: data.first_name,
      last_name: data.last_name,
      create_date: data.create_date,
      role: data.role || 'user',
      tokens: data.tokens
    };

    localStorage.setItem('user', JSON.stringify(userData));

    if (data.tokens) {
      persistTokens(data.tokens);
      startAuthSessionRefresh();
    }

    if (setUser) {
      setUser(userData);
    }

    if (data.email) {
      clearCachedPortfolioMap(data.email);
      clearCachedWatchlistMap(data.email);
      clearCachedFilterPresets(data.email);

      authFetch(`/api/portfolio/${data.email}`)
        .then(res => {
          if (!res.ok) return null;
          return res.json();
        })
        .then(portfolioData => {
          if (!portfolioData) return;
          const portfolioMap = portfolioData.portfolioMap || {};
          if (Object.keys(portfolioMap).length > 0) {
            setCachedPortfolioMap(data.email, portfolioMap);
          }
        })
        .catch(err => console.error('Error caching portfolio:', err));

      authFetch(`/api/watchlist/${data.email}`)
        .then(res => {
          if (!res.ok) return null;
          return res.json();
        })
        .then(watchlistData => {
          if (!watchlistData) return;
          const watchlistMap = watchlistData.watchlistMap || {};
          if (Object.keys(watchlistMap).length > 0) {
            setCachedWatchlistMap(data.email, watchlistMap);
          }
        })
        .catch(err => console.error('Error caching watchlist:', err));

      hydrateFilterPresets(data.email).catch((err) => {
        console.error('Error hydrating filter presets:', err);
      });
    }

    navigate('/');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    clearMessage();

    const url = isLogin ? '/api/login' : '/api/signup';
    const mode = isLogin ? 'login' : 'signup';

    try {
      const requestBody = isLogin
        ? { email: formData.email, password: formData.password }
        : {
            email: formData.email,
            password: formData.password,
            first_name: formData.first_name,
            last_name: formData.last_name,
            country: formData.country
          };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        if (!isLogin) {
          if (!data.userConfirmed) {
            setConfirmationEmail(formData.email);
            setPendingPassword(formData.password);
            setPendingCountry(formData.country);
            setShowConfirmation(true);
            showMessage('success', 'Account created! Check your email for a 6-digit confirmation code.');
          } else {
            try {
              const loginResponse = await fetch('/api/login', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  email: formData.email,
                  password: formData.password
                }),
              });

              const loginData = await loginResponse.json().catch(() => ({}));

              if (loginResponse.ok) {
                handleLoginSuccess(loginData);
              } else if (loginData.needsNewPassword && loginData.session) {
                setConfirmationEmail(loginData.email || formData.email);
                setChallengeSession(loginData.session);
                setChallengeUsername(loginData.username || loginData.email || formData.email);
                setFormData((prev) => ({ ...prev, password: '' }));
                setConfirmPassword('');
                setShowNewPasswordRequired(true);
                showMessage('info', getAuthErrorMessage(loginData, 'newPassword'), { persist: true });
              } else {
                setIsLogin(true);
                setSearchParams({}, { replace: true });
                setFormData({ ...emptyForm(), email: formData.email });
                showMessage('info', 'Account created! Please sign in with your email and password.');
              }
            } catch (loginError) {
              console.error('Auto-login error:', loginError);
              setIsLogin(true);
              setSearchParams({}, { replace: true });
              setFormData({ ...emptyForm(), email: formData.email });
              showMessage('info', 'Account created! Please sign in with your email and password.');
            }
          }
        } else {
          handleLoginSuccess(data);
        }
      } else {
        if (data.needsConfirmation && data.email) {
          setConfirmationEmail(data.email);
          setPendingPassword(formData.password);
          setPendingCountry(formData.country || '');
          setShowConfirmation(true);
          setIsLogin(false);
          showMessage('info', getAuthErrorMessage(data, mode), { persist: true });
        } else if (data.needsNewPassword && data.session) {
          setConfirmationEmail(data.email || formData.email);
          setChallengeSession(data.session);
          setChallengeUsername(data.username || data.email || formData.email);
          setFormData((prev) => ({ ...prev, password: '' }));
          setConfirmPassword('');
          setShowNewPasswordRequired(true);
          showMessage('info', getAuthErrorMessage(data, 'newPassword'), { persist: true });
        } else {
          showMessage('error', getAuthErrorMessage(data, mode), { persist: true });
        }
      }
    } catch (error) {
      console.error('Error:', error);
      showMessage(
        'error',
        isLogin
          ? 'Network error while signing in. Check your connection and try again.'
          : 'Network error while creating your account. Check your connection and try again.',
        { persist: true }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmSignup = async (e) => {
    e.preventDefault();

    if (!confirmationCode.trim()) {
      showMessage('error', 'Enter the 6-digit confirmation code from your email.', { persist: true });
      return;
    }

    setIsLoading(true);
    clearMessage();

    try {
      const response = await fetch('/api/confirm-signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: confirmationEmail,
          confirmationCode: confirmationCode,
          country: pendingCountry || undefined
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok || data.alreadyConfirmed) {
        const email = confirmationEmail;
        const password = pendingPassword;

        if (password) {
          try {
            const loginResponse = await fetch('/api/login', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                email: email,
                password: password
              }),
            });

            const loginData = await loginResponse.json().catch(() => ({}));

            if (loginResponse.ok) {
              handleLoginSuccess(loginData);
              setShowConfirmation(false);
              setConfirmationCode('');
              setConfirmationEmail('');
              setPendingPassword('');
              setPendingCountry('');
              setFormData(emptyForm());
            } else if (loginData.needsNewPassword && loginData.session) {
              setShowConfirmation(false);
              setConfirmationCode('');
              setPendingPassword('');
              setPendingCountry('');
              setChallengeSession(loginData.session);
              setChallengeUsername(loginData.username || loginData.email || email);
              setFormData({ ...emptyForm(), email });
              setConfirmPassword('');
              setShowNewPasswordRequired(true);
              showMessage('info', getAuthErrorMessage(loginData, 'newPassword'), { persist: true });
            } else {
              setShowConfirmation(false);
              setConfirmationCode('');
              setConfirmationEmail('');
              setPendingPassword('');
              setPendingCountry('');
              setIsLogin(true);
              setSearchParams({}, { replace: true });
              setFormData({ ...emptyForm(), email });
              showMessage('success', 'Email verified! Please sign in to continue.');
            }
          } catch (loginError) {
            console.error('Auto-login error after confirmation:', loginError);
            setShowConfirmation(false);
            setConfirmationCode('');
            setConfirmationEmail('');
            setPendingPassword('');
            setPendingCountry('');
            setIsLogin(true);
            setSearchParams({}, { replace: true });
            setFormData({ ...emptyForm(), email });
            showMessage('success', 'Email verified! Please sign in to continue.');
          }
        } else {
          setShowConfirmation(false);
          setConfirmationCode('');
          setConfirmationEmail('');
          setPendingCountry('');
          setIsLogin(true);
          setSearchParams({}, { replace: true });
          setFormData({ ...emptyForm(), email });
          showMessage('success', 'Email verified! Please sign in to continue.');
        }
      } else {
        showMessage('error', getAuthErrorMessage(data, 'signup'), { persist: true });
      }
    } catch (error) {
      console.error('Error:', error);
      showMessage('error', 'Network error while verifying your email. Please try again.', { persist: true });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/resend-confirmation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: confirmationEmail
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        showMessage('success', 'A new confirmation code was sent to your email. Check your inbox and spam folder.');
      } else {
        showMessage('error', getAuthErrorMessage(data, 'signup'), { persist: true });
      }
    } catch (error) {
      console.error('Error:', error);
      showMessage('error', 'Could not resend the confirmation code. Check your connection and try again.', { persist: true });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordRequest = async (e) => {
    e.preventDefault();

    const email = (formData.email || '').trim().toLowerCase();
    if (!email || !isValidEmail(email)) {
      setErrors({ email: 'Enter a valid email address' });
      showMessage('error', 'Enter a valid email address to continue.', { persist: true });
      return;
    }

    setIsLoading(true);
    clearMessage();
    setErrors({});

    try {
      const response = await fetch('/api/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setConfirmationEmail(email);
        setConfirmationCode('');
        setFormData((prev) => ({ ...prev, email, password: '' }));
        setConfirmPassword('');
        setShowForgotPassword(false);
        setShowForgotConfirm(true);
        showMessage('success', 'If an account exists for that email, a reset code was sent. Check your inbox and spam folder.');
      } else {
        showMessage('error', getAuthErrorMessage(data, 'forgot'), { persist: true });
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      showMessage('error', 'Network error while requesting a reset code. Check your connection and try again.', { persist: true });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmForgotPassword = async (e) => {
    e.preventDefault();

    if (!validateNewPasswordForm({ requireCode: true })) {
      return;
    }

    setIsLoading(true);
    clearMessage();

    try {
      const response = await fetch('/api/confirm-forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: confirmationEmail,
          confirmationCode: confirmationCode.trim(),
          newPassword: formData.password,
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        const email = confirmationEmail;
        resetToSignIn(email);
        showMessage('success', 'Password reset successfully. Sign in with your new password.');
      } else {
        showMessage('error', getAuthErrorMessage(data, 'forgot'), { persist: true });
      }
    } catch (error) {
      console.error('Confirm forgot password error:', error);
      showMessage('error', 'Network error while resetting your password. Please try again.', { persist: true });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendForgotPassword = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: confirmationEmail }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        showMessage('success', 'A new reset code was sent to your email. Check your inbox and spam folder.');
      } else {
        showMessage('error', getAuthErrorMessage(data, 'forgot'), { persist: true });
      }
    } catch (error) {
      console.error('Resend forgot password error:', error);
      showMessage('error', 'Could not resend the reset code. Check your connection and try again.', { persist: true });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompleteNewPassword = async (e) => {
    e.preventDefault();

    if (!validateNewPasswordForm()) {
      return;
    }

    if (!challengeSession) {
      showMessage('error', 'This password reset session expired. Please sign in again to continue.', { persist: true });
      resetToSignIn(confirmationEmail);
      return;
    }

    setIsLoading(true);
    clearMessage();

    try {
      const response = await fetch('/api/complete-new-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: confirmationEmail,
          username: challengeUsername || confirmationEmail,
          newPassword: formData.password,
          session: challengeSession,
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        handleLoginSuccess(data);
      } else {
        showMessage('error', getAuthErrorMessage(data, 'newPassword'), { persist: true });
        if (response.status === 401) {
          resetToSignIn(confirmationEmail);
        }
      }
    } catch (error) {
      console.error('Complete new password error:', error);
      showMessage('error', 'Network error while setting your password. Please try again.', { persist: true });
    } finally {
      setIsLoading(false);
    }
  };

  const switchMode = (loginMode) => {
    setIsLogin(loginMode);
    setFormData(emptyForm());
    setConfirmPassword('');
    setErrors({});
    clearMessage();
    setSearchParams(loginMode ? {} : { mode: 'signup' }, { replace: true });
  };

  const openForgotPassword = () => {
    setShowForgotPassword(true);
    setShowForgotConfirm(false);
    setShowNewPasswordRequired(false);
    setShowConfirmation(false);
    setConfirmationCode('');
    setConfirmPassword('');
    setFormData((prev) => ({ ...emptyForm(), email: prev.email }));
    setErrors({});
    clearMessage();
  };

  const renderPasswordRequirements = () => (
    <ul className="password-requirements" aria-label="Password requirements">
      {[
        { key: 'minLength', label: 'Min 8 characters' },
        { key: 'hasNumber', label: 'At least 1 number' },
        { key: 'hasSpecial', label: 'At least 1 special character' },
        { key: 'hasUppercase', label: 'At least 1 uppercase letter' },
        { key: 'hasLowercase', label: 'At least 1 lowercase letter' },
      ].map(({ key, label }) => {
        const met = passwordChecks[key];
        return (
          <li
            key={key}
            className={met ? 'requirement-met' : 'requirement-unmet'}
          >
            <span className={`requirement-box${met ? ' requirement-box--met' : ''}`} aria-hidden>
              {met ? <Check size={12} strokeWidth={3} /> : null}
            </span>
            {label}
          </li>
        );
      })}
    </ul>
  );

  const renderPasswordField = ({
    id = 'password',
    label = 'Password',
    name = 'password',
    value,
    onChange,
    autoComplete = 'new-password',
    error,
    showToggleState,
    setShowToggleState,
  }) => (
    <div className="form-group-modern">
      <label htmlFor={id} className="form-label-modern">{label}</label>
      <div className="auth-input-wrap auth-input-wrap--password">
        <Lock className="auth-input-icon" size={18} aria-hidden />
        <input
          type={showToggleState ? 'text' : 'password'}
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          className={`form-input-modern ${error ? 'form-input-error' : ''}`}
          required
          disabled={isLoading}
          autoComplete={autoComplete}
        />
        <button
          type="button"
          className="auth-password-toggle"
          onClick={() => setShowToggleState((v) => !v)}
          disabled={isLoading}
          aria-label={showToggleState ? 'Hide password' : 'Show password'}
        >
          {showToggleState ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
      {error && <span className="form-error">{error}</span>}
    </div>
  );

  const brandPanel = (
    <aside className="auth-brand-panel">
      <div className="auth-brand-inner">
        <div className="auth-logo-mark auth-logo-mark--brand">
          <BidsigLogo size={52} color="#ffffff" className="bidsig-logo-svg" aria-hidden />
          <span className="auth-logo-wordmark">Bidsig</span>
        </div>
        <h2 className="auth-brand-title">Research smarter. Track with confidence.</h2>
        <p className="auth-brand-desc">
          Screen stocks, follow buy/sell/hold signals, and manage portfolios — all in one place.
        </p>
        <ul className="auth-brand-features">
          <li><TrendingUp size={18} aria-hidden /> Real-time signal screening</li>
          <li><BarChart3 size={18} aria-hidden /> Momentum & mean reversion strategies</li>
          <li><Briefcase size={18} aria-hidden /> Portfolio & watchlist tools</li>
        </ul>
      </div>
    </aside>
  );

  const formCard = (children) => (
    <div className="auth-form-panel">
      <div className="auth-card">
        {children}
      </div>
    </div>
  );

  const messageBanner = message.text ? (
    <div className={`auth-message auth-message-${message.type}`} role="alert">
      <div className="auth-message-body">
        <span className="auth-message-text">{message.text}</span>
        {message.type === 'error' && (
          <button
            type="button"
            className="auth-message-dismiss"
            onClick={clearMessage}
            aria-label="Dismiss message"
          >
            Dismiss
          </button>
        )}
      </div>
    </div>
  ) : null;

  if (showNewPasswordRequired) {
    return (
      <div className="auth-container">
        <div className="auth-layout">
          {brandPanel}
          {formCard(
            <>
              <div className="auth-verify-icon" aria-hidden>
                <Lock size={32} />
              </div>
              <div className="auth-header auth-header--compact">
                <h1 className="auth-title">Set a new password</h1>
                <p className="auth-subtitle">Your account requires a password update before you can sign in.</p>
                <p className="auth-email">{confirmationEmail}</p>
              </div>

              {messageBanner}

              <form onSubmit={handleCompleteNewPassword} className="auth-form" noValidate>
                {renderPasswordField({
                  id: 'newPassword',
                  label: 'New password',
                  name: 'password',
                  value: formData.password,
                  onChange: handleChange,
                  autoComplete: 'new-password',
                  error: errors.password,
                  showToggleState: showPassword,
                  setShowToggleState: setShowPassword,
                })}
                {renderPasswordRequirements()}
                {renderPasswordField({
                  id: 'confirmNewPassword',
                  label: 'Confirm new password',
                  name: 'confirmPassword',
                  value: confirmPassword,
                  onChange: (e) => {
                    setConfirmPassword(e.target.value);
                    if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: '' });
                    if (message.type === 'error') clearMessage();
                  },
                  autoComplete: 'new-password',
                  error: errors.confirmPassword,
                  showToggleState: showConfirmPassword,
                  setShowToggleState: setShowConfirmPassword,
                })}

                <button
                  type="submit"
                  className="auth-button auth-button-primary"
                  disabled={isLoading || !canSubmitNewPassword}
                >
                  {isLoading ? 'Saving…' : 'Save password & sign in'}
                </button>
              </form>

              <div className="auth-footer">
                <button
                  type="button"
                  onClick={() => {
                    resetToSignIn(confirmationEmail);
                    clearMessage();
                  }}
                  className="auth-link-button"
                >
                  Back to sign in
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (showForgotConfirm) {
    return (
      <div className="auth-container">
        <div className="auth-layout">
          {brandPanel}
          {formCard(
            <>
              <div className="auth-verify-icon" aria-hidden>
                <ShieldCheck size={32} />
              </div>
              <div className="auth-header auth-header--compact">
                <h1 className="auth-title">Reset your password</h1>
                <p className="auth-subtitle">Enter the 6-digit code we sent to</p>
                <p className="auth-email">{confirmationEmail}</p>
              </div>

              {messageBanner}

              <p className="auth-notice">
                If you don’t see the email, check spam/junk. Codes expire after a short time — use Resend if needed.
              </p>

              <form onSubmit={handleConfirmForgotPassword} className="auth-form" noValidate>
                <div className="form-group-modern">
                  <label htmlFor="resetCode" className="form-label-modern">Reset code</label>
                  <div className="auth-input-wrap">
                    <ShieldCheck className="auth-input-icon" size={18} aria-hidden />
                    <input
                      type="text"
                      id="resetCode"
                      name="resetCode"
                      value={confirmationCode}
                      onChange={(e) => {
                        setConfirmationCode(e.target.value);
                        if (errors.confirmationCode) setErrors({ ...errors, confirmationCode: '' });
                        if (message.type === 'error') clearMessage();
                      }}
                      placeholder="000000"
                      className={`form-input-modern auth-input-code ${errors.confirmationCode ? 'form-input-error' : ''}`}
                      required
                      maxLength="6"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      disabled={isLoading}
                    />
                  </div>
                  {errors.confirmationCode && <span className="form-error">{errors.confirmationCode}</span>}
                </div>

                {renderPasswordField({
                  id: 'forgotNewPassword',
                  label: 'New password',
                  name: 'password',
                  value: formData.password,
                  onChange: handleChange,
                  autoComplete: 'new-password',
                  error: errors.password,
                  showToggleState: showPassword,
                  setShowToggleState: setShowPassword,
                })}
                {renderPasswordRequirements()}
                {renderPasswordField({
                  id: 'forgotConfirmPassword',
                  label: 'Confirm new password',
                  name: 'confirmPassword',
                  value: confirmPassword,
                  onChange: (e) => {
                    setConfirmPassword(e.target.value);
                    if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: '' });
                    if (message.type === 'error') clearMessage();
                  },
                  autoComplete: 'new-password',
                  error: errors.confirmPassword,
                  showToggleState: showConfirmPassword,
                  setShowToggleState: setShowConfirmPassword,
                })}

                <button
                  type="submit"
                  className="auth-button auth-button-primary"
                  disabled={isLoading || !canSubmitNewPassword || !confirmationCode.trim()}
                >
                  {isLoading ? 'Resetting…' : 'Reset password'}
                </button>
              </form>

              <div className="auth-footer auth-footer--stacked">
                <button
                  type="button"
                  onClick={handleResendForgotPassword}
                  className="auth-link-button"
                  disabled={isLoading}
                >
                  Resend code
                </button>
                <button
                  type="button"
                  onClick={() => {
                    resetToSignIn(confirmationEmail);
                    clearMessage();
                  }}
                  className="auth-link-button"
                >
                  Back to sign in
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (showForgotPassword) {
    return (
      <div className="auth-container">
        <div className="auth-layout">
          {brandPanel}
          {formCard(
            <>
              <div className="auth-header auth-header--compact">
                <h1 className="auth-title">Forgot password</h1>
                <p className="auth-subtitle">
                  Enter your email and we’ll send a code so you can set a new password.
                  Migrated accounts should use this to create their password.
                </p>
              </div>

              {messageBanner}

              <form onSubmit={handleForgotPasswordRequest} className="auth-form" noValidate>
                <div className="form-group-modern">
                  <label htmlFor="forgotEmail" className="form-label-modern">Email</label>
                  <div className="auth-input-wrap">
                    <Mail className="auth-input-icon" size={18} aria-hidden />
                    <input
                      type="email"
                      id="forgotEmail"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`form-input-modern ${errors.email ? 'form-input-error' : ''}`}
                      required
                      disabled={isLoading}
                      autoComplete="email"
                    />
                  </div>
                  {errors.email && <span className="form-error">{errors.email}</span>}
                </div>

                <button
                  type="submit"
                  className="auth-button auth-button-primary"
                  disabled={isLoading}
                >
                  {isLoading ? 'Sending…' : 'Send reset code'}
                </button>
              </form>

              <div className="auth-footer">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    clearMessage();
                  }}
                  className="auth-link-button"
                >
                  Back to sign in
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (showConfirmation) {
    return (
      <div className="auth-container">
        <div className="auth-layout">
          {brandPanel}
          {formCard(
            <>
              <div className="auth-verify-icon" aria-hidden>
                <ShieldCheck size={32} />
              </div>
              <div className="auth-header auth-header--compact">
                <h1 className="auth-title">Verify your email</h1>
                <p className="auth-subtitle">We sent a 6-digit code to</p>
                <p className="auth-email">{confirmationEmail}</p>
              </div>

              {messageBanner}

              <p className="auth-notice">
                If you don’t see the email, check spam/junk. Codes expire after a short time — use Resend if needed.
              </p>

              <form onSubmit={handleConfirmSignup} className="auth-form" noValidate>
                <div className="form-group-modern">
                  <label htmlFor="confirmationCode" className="form-label-modern">Confirmation code</label>
                  <div className="auth-input-wrap">
                    <ShieldCheck className="auth-input-icon" size={18} aria-hidden />
                    <input
                      type="text"
                      id="confirmationCode"
                      name="confirmationCode"
                      value={confirmationCode}
                      onChange={(e) => {
                        setConfirmationCode(e.target.value);
                        if (message.type === 'error') clearMessage();
                      }}
                      placeholder="000000"
                      className="form-input-modern auth-input-code"
                      required
                      maxLength="6"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="auth-button auth-button-primary"
                  disabled={isLoading}
                >
                  {isLoading ? 'Verifying…' : 'Verify account'}
                </button>
              </form>

              <div className="auth-footer auth-footer--stacked">
                <button
                  type="button"
                  onClick={handleResendConfirmation}
                  className="auth-link-button"
                  disabled={isLoading}
                >
                  Resend code
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowConfirmation(false);
                    setConfirmationCode('');
                    setConfirmationEmail('');
                    clearMessage();
                  }}
                  className="auth-link-button"
                >
                  Back to sign in
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-layout">
        {brandPanel}
        {formCard(
          <>
            <div className="auth-mode-tabs" role="tablist" aria-label="Account mode">
              <button
                type="button"
                role="tab"
                aria-selected={isLogin}
                className={`auth-mode-tab${isLogin ? ' active' : ''}`}
                onClick={() => switchMode(true)}
                disabled={isLoading}
              >
                Sign in
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={!isLogin}
                className={`auth-mode-tab${!isLogin ? ' active' : ''}`}
                onClick={() => switchMode(false)}
                disabled={isLoading}
              >
                Sign up
              </button>
            </div>

            <div className="auth-header auth-header--compact">
              <h1 className="auth-title">
                {isLogin ? 'Welcome back' : 'Create your account'}
              </h1>
              <p className="auth-subtitle">
                {isLogin
                  ? 'Sign in to access your screener, portfolio, and alerts'
                  : 'Join Bidsig to track holdings and access more features'}
              </p>
            </div>

            {messageBanner}

            <form onSubmit={handleSubmit} className="auth-form" noValidate>
              {!isLogin && (
                <div className="form-row form-row--names">
                  <div className="form-group-modern">
                    <label htmlFor="first_name" className="form-label-modern">First name</label>
                    <div className="auth-input-wrap">
                      <User className="auth-input-icon" size={18} aria-hidden />
                      <input
                        type="text"
                        id="first_name"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        className={`form-input-modern ${errors.first_name ? 'form-input-error' : ''}`}
                        required
                        disabled={isLoading}
                        autoComplete="given-name"
                      />
                    </div>
                    {errors.first_name && <span className="form-error">{errors.first_name}</span>}
                  </div>

                  <div className="form-group-modern">
                    <label htmlFor="last_name" className="form-label-modern">Last name</label>
                    <div className="auth-input-wrap">
                      <User className="auth-input-icon" size={18} aria-hidden />
                      <input
                        type="text"
                        id="last_name"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        className={`form-input-modern ${errors.last_name ? 'form-input-error' : ''}`}
                        required
                        disabled={isLoading}
                        autoComplete="family-name"
                      />
                    </div>
                    {errors.last_name && <span className="form-error">{errors.last_name}</span>}
                  </div>

                  {showNameLengthHint && (
                    <p className="form-hint-modern auth-name-hint">Minimum of 2 letters required</p>
                  )}
                </div>
              )}

              {!isLogin && (
                <div className="form-group-modern">
                  <label htmlFor="country" className="form-label-modern">Country</label>
                  <div className="auth-input-wrap">
                    <Globe className="auth-input-icon" size={18} aria-hidden />
                    <select
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className={`form-input-modern auth-select ${errors.country ? 'form-input-error' : ''}${!formData.country ? ' auth-select--placeholder' : ''}`}
                      required
                      disabled={isLoading}
                      autoComplete="country-name"
                    >
                      <option value="" disabled>
                        Select country
                      </option>
                      {COUNTRY_OPTIONS.map(({ value, label }) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {errors.country && <span className="form-error">{errors.country}</span>}
                </div>
              )}

              <div className="form-group-modern">
                <label htmlFor="email" className="form-label-modern">Email</label>
                <div className="auth-input-wrap">
                  <Mail className="auth-input-icon" size={18} aria-hidden />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`form-input-modern ${errors.email ? 'form-input-error' : ''}`}
                    required
                    disabled={isLoading}
                    autoComplete="email"
                  />
                </div>
                {errors.email && <span className="form-error">{errors.email}</span>}
              </div>

              <div className="form-group-modern">
                <label htmlFor="password" className="form-label-modern">Password</label>
                <div className="auth-input-wrap auth-input-wrap--password">
                  <Lock className="auth-input-icon" size={18} aria-hidden />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`form-input-modern ${errors.password ? 'form-input-error' : ''}`}
                    required
                    disabled={isLoading}
                    autoComplete={isLogin ? 'current-password' : 'new-password'}
                  />
                  <button
                    type="button"
                    className="auth-password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    disabled={isLoading}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.password && <span className="form-error">{errors.password}</span>}
                {!isLogin && renderPasswordRequirements()}
              </div>

              {isLogin && (
                <div className="auth-forgot-row">
                  <button
                    type="button"
                    className="auth-link-button"
                    onClick={openForgotPassword}
                    disabled={isLoading}
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              <button
                type="submit"
                className="auth-button auth-button-primary"
                disabled={isLoading || (!isLogin && !canCreateAccount)}
              >
                {isLoading
                  ? (isLogin ? 'Signing in…' : 'Signing up…')
                  : (isLogin ? 'Sign in' : 'Sign up')}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
